/* mounting.js — <pdp-mounting>: the binding-mounting radio cards and the
   boot-sole-length stepper.

   Lives in its own file because TWO surfaces render it and they must not
   drift: the product page (`sections/main-product.liquid`) and the quick-view
   dialog (`sections/quick-view.liquid`), both through
   `snippets/product-mounting.liquid`. Same reasoning as
   `configurator-picker.js`, which the quick view also loads.

   The quick view is injected with the Section Rendering API, and
   `quick-view.js` clones only <link> tags out of the fetched body — scripts in
   it never execute. So every host that can open a quick view must load this
   file itself, exactly like it loads `quick-view.js`.

   Mounting is the ONLY place a ski line item gets its `Bindungsmontage`
   property, so an unupgraded element means a line with no mounting flag at
   all — the ambiguous blank the workshop cannot interpret. Keep the custom
   element definition unconditional and cheap.

   Three choices since 2026-09-13 (docs/mounting/contract.md):
     mounted    erwünscht + Sohlenlänge (mm), the length input is required
     pending    erwünscht + Schuhanpassung: Sohlenlänge folgt + _fit: pending,
                two hidden inputs the card carries, enabled only while chosen;
                rendered only with the theme setting mounting_bsl_pending_enabled
     unmounted  nicht erwünscht, nothing else
   Only `mounted` needs the sole length; the other two always satisfy the
   submit guard. The snippet preselects `pending` since 12.10.2026
   (`unmounted` with the setting off) and renders the length input disabled
   and the pending pair enabled to match; this element reads whatever radio
   is checked, so nothing here depends on the preselection.

   Shoe size instead of sole length (2026-09-17, orders #1594 and #1101): a
   valid length that reads like a Mondopoint size (a boot of that size in the
   cart, or under 240 mm on an adult ski since 12.10.2026) shows the calm
   hint from snippets/sole-length-help.liquid. It never touches `satisfied`
   or the submit guard. The rule lives in assets/mounting-properties.js and
   arrives by DYNAMIC import on purpose: this file must keep upgrading the
   element where import maps are missing (Safari <= 16.3), because without it
   a ski line carries no mounting answer at all. No rule, no hint, nothing
   else changes.
*/

(() => {
  /* -------------------------------------------------------------------------
     Binding mounting — radio cards + boot sole length
  ------------------------------------------------------------------------- */

  if (!customElements.get('pdp-mounting')) {
    customElements.define(
      'pdp-mounting',
      class PdpMounting extends HTMLElement {
        connectedCallback() {
          this.bslInput = this.querySelector('[data-bsl-input]');

          // Whole card selects its radio (labels already cover the header row).
          this.querySelectorAll('[data-mounting-card]').forEach((card) => {
            card.addEventListener('click', (event) => {
              if (event.target.closest('input, button, a, label, select')) return;
              const radio = card.querySelector('input[type="radio"]');
              if (radio && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          });

          this.addEventListener('change', (event) => {
            if (event.target.matches('[data-mounting-choice]')) this.syncBsl();
          });

          this.querySelectorAll('[data-bsl-step]').forEach((button) => {
            button.addEventListener('click', () => this.step(parseInt(button.dataset.bslStep, 10)));
          });

          // The short route beside the empty field selects the existing pending
          // card, including its explanatory copy and unchanged order properties.
          this.querySelector('[data-bsl-pending]')?.addEventListener('click', () => {
            const pending = this.querySelector('[data-mounting-choice="pending"]');
            if (!pending) return;
            pending.checked = true;
            pending.dispatchEvent(new Event('change', { bubbles: true }));
            pending.focus({ preventScroll: true });
            pending.closest('[data-mounting-card]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          });

          if (this.bslInput) {
            // No clamping on blur: a value outside the binding's range (150 on a
            // 200 to 380 card) must stay visible with the inline error and keep
            // the add-to-cart blocked, not be silently rewritten to the minimum
            // and sent to the workshop (Q-PDP-06). Only the steppers clamp.
            this.bslInput.addEventListener('blur', () => {
              const reveal = this.bslInput.value.trim() !== '' && !this.satisfied;
              this.refresh({ reveal });
            });
            this.bslInput.addEventListener('input', () => this.refresh());
            // Belt and braces: a disabled input is already excluded from FormData,
            // and refresh() blocks submitting a blank one, so this only guards
            // against an empty value ever reaching the cart as "".
            this.bslInput.form?.addEventListener('formdata', (event) => {
              // The pending pair travels only with the pending card; disabled
              // inputs are excluded already, this catches a re-enabled one.
              if (this.choice !== 'pending') {
                this.pendingInputs.forEach((input) => event.formData.delete(input.name));
              }
              if (this.bslInput.disabled) return;
              if (!this.bslInput.value.trim()) event.formData.delete(this.bslInput.name);
            });

            // Choosing "mount before dispatch" makes the sole length mandatory.
            // The guard lives in the CAPTURE phase on an ancestor because
            // product-form.js binds submit on the <form> itself (product-form.js#L10)
            // and does not check defaultPrevented — at the target, listeners run in
            // registration order, so a bubble-phase listener here would fire too
            // late to stop the fetch.
            const form = this.bslInput.form;
            this.guardHost = form?.closest('product-form') || form?.parentElement || null;
            this.mainButton = this.guardHost?.querySelector('[type="submit"]');
            if (this.guardHost) {
              this.guardHost.addEventListener('click', (event) => {
                if (!this.mainButton?.contains(event.target) || this.satisfied || !this.canGuide) return;
                event.preventDefault();
                event.stopPropagation();
                this.revealMissingSoleLength();
              }, true);
              this.guardHost.addEventListener(
                'submit',
                (event) => {
                  if (this.satisfied) return;
                  event.preventDefault();
                  event.stopPropagation();
                  if (this.canGuide) this.revealMissingSoleLength();
                },
                true
              );
            }

            // Stock checks and asynchronous cart requests own disabled/loading.
            // Refresh the navigation affordance when either changes; never clear
            // their aria-disabled flag just because mounting is complete.
            if (this.mainButton) {
              this.buttonObserver = new MutationObserver(() => this.refresh());
              this.buttonObserver.observe(this.mainButton, {
                attributes: true,
                attributeFilter: ['disabled', 'aria-disabled', 'class'],
                childList: true,
                subtree: true,
                characterData: true,
              });
            }
          }

          // Dawn re-runs toggleSubmitButton() from the freshly fetched HTML on every
          // variant change (product-info.js#L215), which resets the button's look and
          // label. Re-assert afterwards so the blocked state survives a size click.
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => {
              requestAnimationFrame(() => this.refresh());
            });
          }

          this.syncBsl();
        }

        disconnectedCallback() {
          this.unsubscribeVariant?.();
          this.buttonObserver?.disconnect();
        }

        get canGuide() {
          return this.mainButton && !this.mainButton.disabled &&
            !this.mainButton.classList.contains('loading') &&
            this.mainButton.getAttribute('aria-disabled') !== 'true';
        }

        revealMissingSoleLength() {
          if (!this.bslInput || this.satisfied) return;
          this.refresh({ reveal: true });
          const raw = this.bslInput.value.trim();
          const reason = raw === '' ? 'empty' : Number.isInteger(Number(raw)) ? 'range' : 'format';
          this.dispatchEvent(new CustomEvent('pdp-mounting:blocked', { bubbles: true, detail: { reason } }));
          this.bslInput.focus({ preventScroll: true });
          this.bslInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        get mountedSelected() {
          return this.querySelector('[data-mounting-choice="mounted"]')?.checked ?? false;
        }

        /* 'mounted' | 'pending' | 'unmounted' | null (nothing checked). */
        get choice() {
          const checked = this.querySelector('[data-mounting-choice]:checked');
          return checked?.dataset?.mountingChoice ?? null;
        }

        get pendingInputs() {
          return Array.from(this.querySelectorAll?.('[data-pending-input]') ?? []);
        }

        syncBsl() {
          if (!this.bslInput) return;
          // A disabled control is excluded from FormData, so unmounted and
          // pending orders never carry a boot sole length property, and only
          // a pending order carries the Schuhanpassung pair.
          const choice = this.choice;
          this.bslInput.disabled = choice !== 'mounted';
          this.pendingInputs.forEach((input) => { input.disabled = choice !== 'pending'; });
          this.refresh();
        }

        /* Mounted + a sole length inside the engraved range is the only state that
           needs the value; "deliver unmounted" and "sole length follows" need
           nothing. */
        get satisfied() {
          if (!this.bslInput || !this.mountedSelected) return true;
          const value = Number(this.bslInput.value);
          if (!this.bslInput.value.trim() || !Number.isInteger(value)) return false;
          const min = parseInt(this.bslInput.min, 10);
          const max = parseInt(this.bslInput.max, 10);
          return value >= min && value <= max;
        }

        /* An incomplete mounting choice is a navigation action, not a disabled
           button. Keep the capture-phase submit guard, but leave disabled and
           aria-disabled exclusively to stock/loading so CSS cannot swallow the
           click that explains what is missing. */
        refresh({ reveal = false } = {}) {
          if (!this.bslInput) return;
          const blocked = !this.satisfied;
          const errorEl = this.querySelector('[data-bsl-error]');

          if (reveal) this.revealed = true;
          if (!blocked) this.revealed = false;
          // Must be a real boolean: `classList.toggle(token, undefined)` counts as
          // "no force argument" and FLIPS the class, so a first load — blocked with
          // `revealed` still unset — used to switch the invalid styling ON and every
          // variant change flipped it again.
          const showError = blocked && this.revealed === true;

          if (errorEl) errorEl.hidden = !showError;
          this.bslInput.setAttribute('aria-invalid', showError ? 'true' : 'false');
          if (this.classList.contains('pdp-mounting--invalid') !== showError) {
            this.classList.toggle('pdp-mounting--invalid', showError);
          }

          const button = this.mainButton;
          if (button) {
            if (button.classList.contains('pdp-submit--blocked') !== blocked) {
              button.classList.toggle('pdp-submit--blocked', blocked);
            }
            const action = blocked && this.canGuide;
            button.toggleAttribute('data-mounting-action', Boolean(action));
            const label = button.querySelector(':scope > span:not([class])');
            const guidance = this.dataset.completeLabel;
            if (label && guidance) {
              if (action) {
                if (label.textContent.trim() !== guidance) {
                  this.submitLabel = label.textContent;
                  label.textContent = guidance;
                }
              } else if (label.textContent.trim() === guidance && this.submitLabel) {
                label.textContent = this.submitLabel;
              }
            }
          }
          this.renderSuspect();
        }

        /* null, or { reason, size } when the accepted length reads like a shoe
           size. Only a value the guard lets through is judged. */
        get suspect() {
          const rule = this.constructor.suspicion;
          if (typeof rule !== 'function' || !this.bslInput || !this.mountedSelected || !this.satisfied) return null;
          return rule(this.bslInput.value, { adult: this.bslInput.dataset?.bslAdult === 'true' });
        }

        renderSuspect() {
          const box = this.querySelector('[data-bsl-suspect]');
          if (!box) return;
          const verdict = this.suspect;
          box.hidden = !verdict;
          if (!verdict) return;
          const text = box.querySelector('[data-bsl-suspect-text]');
          const copy = box.dataset.copySize || '';
          if (text) text.textContent = copy.split('[mm]').join(this.bslInput.value.trim()).split('[size]').join(verdict.size);
        }

        step(direction) {
          if (!this.bslInput) return;
          const min = parseInt(this.bslInput.min, 10);
          const max = parseInt(this.bslInput.max, 10);
          const fallback = Math.round((min + max) / 2);
          const current = parseInt(this.bslInput.value, 10);
          const next = Number.isNaN(current) ? fallback : current + direction;
          this.bslInput.value = Math.min(max, Math.max(min, next));
          this.bslInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    );
  }

  import('#mounting-properties')
    .then((rules) => {
      customElements.get('pdp-mounting').suspicion = rules.soleLengthSuspicion;
      document.querySelectorAll?.('pdp-mounting').forEach((element) => element.refresh?.());
    })
    .catch(() => {});
})();
